import {FileType, IFile, IFileSystemState} from "./types";
import {union} from "lodash";
import {IdObjectsWithId} from "../../utils";

export const count = (files: { [id: string]: IFile }, file: IFile) => {
    let number = 0;
    if (file.parentId) {
        const parent = files[file.parentId]
        if (parent && parent.children) {
            parent.children.forEach(id => {
                const sibling = files[id]
                if (sibling) {
                    if (sibling.name === file.name && sibling.type === file.type) {
                        number++
                    }
                }
            })
        }
    }
    return number;
};

export const loadingFiles = (state: IFileSystemState): IFileSystemState => {
    return {
        ...state,
        isFilesLoading: true
    }
}

export const mergeFiles = (state: IFileSystemState, files: IdObjectsWithId<IFile>): IFileSystemState => {
    files = Object.keys(files.byId).reduce((result:IdObjectsWithId<IFile>, id) : IdObjectsWithId<IFile> => {
        const file = files.byId[id]
        if (file.children) {
            file.children = file.children.sort((a, b):number => {
                const fileA = files.byId[a]
                const fileB = files.byId[b]
                if (!fileA) {
                    return -1
                }
                if (!fileB) {
                    return 1
                }
                if (fileA.type != fileB.type) {
                    if (fileA.type === FileType.FILE) {
                        return -1
                    }

                    if (fileA.type === FileType.FOLDER) {
                        return 1
                    }
                }
                if (fileA.name === "html" && fileB.name === "scss") {
                    return 1
                }
                if (fileA.name === "scss" && fileB.name === "html") {
                    return -1
                }
                if (fileA.name > fileB.name) {
                    return 1
                }
                if (fileA.name < fileB.name) {
                    return -1
                }
                return 0
            })
        }
        result.byId[id] = file
        return result
    }, {byId:{}})

    return {
        ...state,
        files: {
            ...files,
            byId: {
                ...state.files.byId,
                ...files.byId
            },
        },
        isFilesLoading: false
    }
}

export const addFile = (state: IFileSystemState, newFile: IFile): IFileSystemState => {
    const files = state.files.byId
    files[newFile.id] = newFile
    if (newFile.parentId) {
        const parent = files[newFile.parentId]
        if (parent) {
            parent.children = union(parent.children, [newFile.id])
        }
    }

    return {
        ...state,
        files: {
            byId: {
                ...files
            },
        },
        isFilesLoading: false
    }
}

export const deleteFile = (state: IFileSystemState, id: string) => {
    const file = state.files.byId[id]
    if (file) {
        if (file.type === FileType.FOLDER && file.children) {
            file.children.forEach((v, i) => {
                deleteFile(state, v)
            })
        }

        let parentId = file.parentId
        if (parentId) {
            let parent = state.files.byId[parentId]
            if (parent && parent.children) {
                let index = parent.children.indexOf(file.id)
                if (index != -1) {
                    parent.children.splice(index, 1)
                }
            }
        }
        delete state.files.byId[file.id]
    }
    return {
        ...state,
        files: {
            byId: {
                ...state.files.byId
            },
        },
        isLoading: false
    }
}

export const findAllCheckedIds = (clickedId: string, checkedIds: string[], files: IdObjectsWithId<IFile>): string[] => {
    const id = clickedId;
    const ids: string[] = [];
    const file = files.byId[id];
    // id is checked, uncheck it
    const uncheck: boolean = checkedIds.includes(id);

    ids.push(id);
    const childrenNames: { [key: string]: boolean } = {};
    if (file.children) {
        file.children.forEach(id => {
            const file = files.byId[id];
            if (file && file.type === FileType.FILE) {
                ids.push(id);
                childrenNames[file.name] = true;
            }
        })
    }

    // check all it's parents ancestor files, till /html or /scss,
    // excluding those names existed in children
    const ancestorResult = processAncestorFileIds(file, files, childrenNames);
    ids.push(...ancestorResult.ids);

    // remove ids under the same root
    const filteredCheckedIds = checkedIds.filter(id => {
        const file = files.byId[id];
        if (file) {
            return !file.path.includes(ancestorResult.root.path)
        }
        return true;
    });
    if (uncheck) {
        return filteredCheckedIds
    }
    ids.push(...filteredCheckedIds);

    return ids;
}

interface IProcessAncestorFileIdsReturn {
    ids: string[];
    root: IFile;
}

// breadth-first traversal so that deeper files having higher priority
// visit parent folder, put it into the queue and check the id, process the parent of parent until react /html or /scss
// iterate the queue, find it's children files, if filename is not existing, add it.
export const processAncestorFileIds = (file: IFile, files: IdObjectsWithId<IFile>, existingFileNames: { [key: string]: boolean }): IProcessAncestorFileIdsReturn => {
    const ids: string[] = [];
    let root: IFile = file;
    const queue: IFile[] = [];
    let parent = file;
    while (parent) {
        if (/^\/.*\/html$/.test(parent.path) || /^\/.*\/scss$/.test(parent.path)) {
            root = parent;
            break
        }
        if (parent.path === '/') {
            break;
        }
        if (!parent.parentId) {
            break;
        }
        parent = files.byId[parent.parentId];
        if (!parent) {
            break;
        }
        queue.push(parent);
        ids.push(parent.id)
    }
    queue.forEach(file => {
        if (file.children) {
            file.children.forEach(id => {
                const child = files.byId[id];
                if (!child) {
                    return
                }
                if (child.type === FileType.FILE && !existingFileNames[child.name]) {
                    ids.push(child.id);
                    existingFileNames[child.name] = true
                }
            })
        }
    });
    return {
        ids: ids,
        root: root,
    };
}
